// Sdílený měsíční sloupcový graf pro Finance taby (Banka/Doklady/AOP/DPH) —
// vždy CELÝ ROK (12 měsíců, chybějící = 0), legenda s ročními součty a hodnoty
// nad sloupci (zkráceně). Cíl: aby všechny finanční taby vypadaly stejně.
//
// props:
//   data:   [{ mesic:'YYYY-MM', <key>:number, ... }]
//   series: [{ key, label, color }]
//   rok?:   číslo/řetězec roku (jinak nejnovější rok z dat, jinak letošní)
//   jednotka?: 'Kč' (default), vyska?: px (default 150)
//   fill?:  když true, graf vyplní výšku rodiče (flex) místo pevné výšky —
//           výška sloupců se počítá procentuálně. Rodič musí mít určenou výšku.

const MESICE = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const fmtPlny = (v) => new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v || 0);
const fmtKratky = (v) => {
    const a = Math.abs(v || 0);
    if (a >= 1_000_000) return ((v / 1_000_000).toFixed(1).replace('.', ',')) + 'M';
    if (a >= 1_000) return Math.round(v / 1_000) + 'k';
    return String(Math.round(v || 0));
};

export default function MesicniGraf({ data = [], series = [], rok, jednotka = 'Kč', vyska = 150, fill = false }) {
    const roky = data.map(d => String(d.mesic || '').slice(0, 4)).filter(Boolean).sort();
    const cilovyRok = String(rok || (roky.length ? roky[roky.length - 1] : new Date().getFullYear()));
    const mapa = Object.fromEntries(data.map(d => [d.mesic, d]));

    const body = MESICE.map(mm => {
        const row = mapa[`${cilovyRok}-${mm}`] || {};
        return { mm, hodnoty: series.map(s => Number(row[s.key]) || 0) };
    });
    const max = Math.max(1, ...body.flatMap(m => m.hodnoty.map(Math.abs)));
    const soucty = series.map((s, i) => body.reduce((sum, m) => sum + m.hodnoty[i], 0));
    const plochaVyska = vyska - 22; // místo pod měsíční popisky

    const Legenda = () => (
        <div className={`flex flex-wrap gap-x-4 gap-y-1 mb-3 ${fill ? 'flex-shrink-0' : ''}`}>
            {series.map((s, i) => (
                <div key={s.key} className="flex items-center gap-1.5 text-xs">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
                    <span className="tabular-nums font-semibold" style={{ color: s.color }}>{fmtPlny(soucty[i])} {jednotka}</span>
                </div>
            ))}
        </div>
    );

    const Sloupec = ({ m, plocha }) => (
        <div className={`flex-1 flex flex-col items-center gap-1 min-w-0 ${fill ? 'h-full' : ''}`}
            title={`${m.mm}/${cilovyRok}: ` + series.map((s, i) => `${s.label} ${fmtPlny(m.hodnoty[i])}`).join(' · ')}>
            <div className={`w-full flex justify-center gap-0.5 ${fill ? 'items-stretch flex-1 min-h-0' : 'items-end'}`}
                style={fill ? undefined : { height: plocha }}>
                {series.map((s, i) => (
                    <div key={s.key} className="flex flex-col items-center justify-end" style={{ flex: 1, height: fill ? undefined : '100%', maxWidth: series.length > 1 ? 16 : 26 }}>
                        {m.hodnoty[i] !== 0 && (
                            <span className="tabular-nums leading-none mb-0.5" style={{ fontSize: 8, color: 'var(--text-tertiary)' }}>{fmtKratky(m.hodnoty[i])}</span>
                        )}
                        <div style={{
                            width: '100%', background: s.color, borderRadius: 2,
                            height: fill
                                ? `${Math.max(2, Math.abs(m.hodnoty[i]) / max * 92)}%`
                                : `${Math.max(1, Math.abs(m.hodnoty[i]) / max * (plocha - 12))}px`,
                        }} />
                    </div>
                ))}
            </div>
            <div className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{m.mm}</div>
        </div>
    );

    // Fill: graf vyplní výšku rodiče (procentuální sloupce). Rodič = flex s výškou.
    if (fill) {
        return (
            <div className="flex flex-col h-full min-h-0">
                <Legenda />
                <div className="flex items-end gap-1 flex-1 min-h-0">
                    {body.map(m => <Sloupec key={m.mm} m={m} />)}
                </div>
            </div>
        );
    }

    return (
        <div>
            <Legenda />
            <div className="flex items-end gap-1" style={{ height: vyska }}>
                {body.map(m => <Sloupec key={m.mm} m={m} plocha={plochaVyska} />)}
            </div>
        </div>
    );
}
